/**
 * components/dashboard/DashboardFormsOverview.tsx
 *
 * Dashboard section showing a read-only overview of the user's most recently
 * updated form templates. No actions are exposed here - navigation to the
 * full Formulaires page is the single entry point for managing forms.
 * Updated (Apr 2026): removed date display and per-row click/hover - rows are
 * purely informational, "Voir tout" is the sole navigation entry point.
 * Updated (Apr 2026): expanded getStatusBadge to handle all known status values
 * explicitly with a safe fallback for unexpected values.
 * Updated (Apr 2026): added is_assigned field to RecentForm; removed "Personnalisé" badge
 * (all forms are now admin-assigned).
 * Updated (Apr 2026): accepts a loading prop and renders a localised spinner so the
 * card header is always visible while data is being fetched, avoiding a full-page block.
 * Updated (May 2026): added last_sent_at to RecentForm interface for sort-by-send-date
 * support in useUserForms. Field is not displayed but documents the sort key.
 */

import { useNavigate } from 'react-router-dom';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { FileText, ArrowRight } from 'lucide-react';

export interface RecentForm {
  id: string;
  name: string;
  status: string;
  updated_at: string;
  last_sent_at: string | null;
  is_assigned: boolean;
}

interface DashboardFormsOverviewProps {
  forms: RecentForm[];
  loading?: boolean;
}

// Expected status values (enforced by DB constraint): 'active' | 'draft'
function getStatusBadge(status: string) {
  if (status === 'active') {
    return <Badge variant="success">Actif</Badge>;
  }
  if (status === 'draft') {
    return <Badge variant="warning">Brouillon</Badge>;
  }
  console.warn(`Unexpected form template status: "${status}"`);
  return <Badge variant="neutral">{status}</Badge>;
}

export default function DashboardFormsOverview({ forms, loading = false }: DashboardFormsOverviewProps) {
  const navigate = useNavigate();

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between mb-0 -mx-6 px-6 pb-4 border-b border-neutral-100">
        <h2 className="text-sm font-semibold text-neutral-900 uppercase tracking-wider">
          Mes formulaires
        </h2>
        <button
          onClick={() => navigate('/my-forms')}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          Voir tout
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="sm" />
        </div>
      ) : forms.length === 0 ? (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-2xl mb-4">
            <FileText className="w-7 h-7 text-neutral-400" />
          </div>
          <p className="text-sm font-medium text-neutral-600 mb-1">Aucun formulaire pour le moment</p>
          <p className="text-xs text-neutral-400 mb-5">
            Créez votre premier formulaire pour commencer à collecter des informations
          </p>
          <Button size="sm" onClick={() => navigate('/my-forms/new')}>
            Créer mon premier formulaire
          </Button>
        </div>
      ) : (
        <div className="-mx-6 -mb-6 mt-0">
          {forms.map((form, index) => (
            <div
              key={form.id}
              className={`flex items-center gap-3 px-6 py-3.5 ${index > 0 ? 'border-t border-neutral-100' : ''}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">{form.name}</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                {getStatusBadge(form.status)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
